/**
 * Utility functions for image compression and manipulation
 */

/**
 * Comprime uma imagem para reduzir o tamanho do arquivo
 * @param {File} file - O arquivo de imagem a comprimir
 * @param {number} maxWidth - Largura máxima (padrão: 1920)
 * @param {number} maxHeight - Altura máxima (padrão: 1920)
 * @param {number} quality - Qualidade da compressão 0-1 (padrão: 0.8)
 * @param {number} maxSizeKB - Tamanho máximo em KB (padrão: 500)
 * @returns {Promise<string>} - Promise que resolve com a string base64 comprimida
 */
export function compressImage(file, maxWidth = 1920, maxHeight = 1920, quality = 0.8, maxSizeKB = 500) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calcular novas dimensões mantendo proporção
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        // Criar canvas para redimensionar e comprimir
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Converter para base64 com compressão
        let compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        let currentSizeKB = (compressedBase64.length * 3) / 4 / 1024; // Aproximação do tamanho em KB
        
        // Se ainda estiver muito grande, reduzir qualidade progressivamente
        if (currentSizeKB > maxSizeKB) {
          let currentQuality = quality;
          const minQuality = 0.3;
          const qualityStep = 0.1;
          
          const tryCompress = () => {
            if (currentQuality < minQuality) {
              // Se não conseguir comprimir mais, usar o resultado atual
              resolve(compressedBase64);
              return;
            }
            
            compressedBase64 = canvas.toDataURL('image/jpeg', currentQuality);
            currentSizeKB = (compressedBase64.length * 3) / 4 / 1024;
            
            if (currentSizeKB > maxSizeKB && currentQuality > minQuality) {
              currentQuality -= qualityStep;
              tryCompress();
            } else {
              resolve(compressedBase64);
            }
          };
          
          tryCompress();
        } else {
          resolve(compressedBase64);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Erro ao carregar imagem'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Erro ao ler arquivo'));
    };
    
    reader.readAsDataURL(file);
  });
}

/**
 * Comprime múltiplas imagens
 * @param {File[]} files - Array de arquivos de imagem
 * @param {number} maxWidth - Largura máxima (padrão: 1920)
 * @param {number} maxHeight - Altura máxima (padrão: 1920)
 * @param {number} quality - Qualidade da compressão 0-1 (padrão: 0.8)
 * @param {number} maxSizeKB - Tamanho máximo em KB por imagem (padrão: 500)
 * @returns {Promise<string[]>} - Promise que resolve com array de strings base64 comprimidas
 */
export async function compressImages(files, maxWidth = 1920, maxHeight = 1920, quality = 0.8, maxSizeKB = 500) {
  const fileArray = Array.from(files || []);
  const compressedImages = await Promise.all(
    fileArray.map(file => compressImage(file, maxWidth, maxHeight, quality, maxSizeKB))
  );
  return compressedImages;
}



